import React from 'react';
import { API_URL } from '../../api/client';
import {
   AhojTableContext,
   ControlState,
   fromControlState,
   fromStructureType,
   getDefaultLigandColor,
   getStructureTypeGroup,
   MolstarAction,
   MolstarRepresentationChangePayload,
   MolstarTransparencyPayload,
   StructureType,
} from '../../contexts/AhojContext';
import { ApoHoloResultRow } from '../../models/ApoHoloResultRow';
import { ApoHoloSiteDto } from '../../models/ApoHoloSiteDto';
import { ApoHoloSiteRow } from '../../models/ApoHoloSiteRow';
import { ColorPairDto } from '../../models/ColorPairDto';
import { SelectStructureDto } from '../../models/SelectStructureDto';
import {
   defaultLigandRepresentation,
   defaultPocketRepresentation,
   defaultRestRepresentation,
   Representation,
   StructureComponentType,
} from '../molstar/Controls/StructureRepresentationComponent';
import { COLOR_PAIRS } from './ColorPairs';
import SitesTableCommonProps from './SitesTableCommonProps';

/**
 * Sortable column keys for result tables. Each key maps to a numeric field of
 * `ApoHoloResultRow` so the generic sort comparator can handle `null` values
 * uniformly (nulls sort last).
 */
export type SortableKey = keyof Pick<
   ApoHoloResultRow,
   | 'resolution'
   | 'mapped_binding_residues_observed'
   | 'mapped_binding_residues_unobserved'
   | 'mapped_binding_residues_percent'
   | 'rmsd'
   | 'pocket_rmsd'
   | 'tm_score'
   | 'sasa'
   | 'volume'
>;

/** Sort direction for result table columns. */
export type SortDir = 'asc' | 'desc';

/**
 * Abstract base class for all five result-table components
 * (HoloSitesTable, ApoSitesTable, UnobservedSitesTable, QuerySitesTable, AlphaFoldModelTable).
 *
 * Implemented as a `React.Component` (class) because the tables need `static contextType`
 * to consume the merged `AhojTableContext` — something hooks cannot do inside a class.
 *
 * Responsibilities:
 * - Row click → Molstar visibility / focus toggle (`selectUnselectStructure`)
 * - Per-row show/hide eye toggle (`showHideStructure`) and batch toggle (`showHideAll`)
 * - Representation and color change dispatch (`changeRepresentation`, `changeColor`)
 * - Quick color-pair assignment and transparency management (`selectNextColorPair`,
 *   `recomputeAllTransparencies`)
 * - Edit / info expandable panel toggling (mutually exclusive)
 * - RCSB sequence viewer selection (`addOrRemoveFromRcsb`)
 * - Column sorting (`handleSort`, `getSortedData`)
 */
export class AhojTableBase extends React.Component<SitesTableCommonProps, typeof AhojTableContext> {
   static contextType = AhojTableContext;
   context: React.ContextType<typeof AhojTableContext>;
   get editingRowId(): string | null {
      return this.context.editingRowId;
   }
   get infoRowId(): string | null {
      return this.context.infoRowId;
   }
   /**
    * When `true`, the next `structureClick` event from the event bus will be
    * ignored. Set to `true` by `selectUnselectStructure` right before it calls
    * `dispatchStructureClick` itself so the table doesn't react to its own click.
    */
   suppressNextStructureClick = false;
   addToRcsbTitle = 'Add to Sequence Viewer';
   removeFromRcsbTitle = 'Remove from Sequence Viewer';
   selectedTitle = 'Selected';

   sortKey: SortableKey | null = null;
   sortDir: SortDir = 'asc';
   sortableThStyle: React.CSSProperties = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
   expandedColumns = false;

   /**
    * Toggles expanded column visibility mode. Uses `forceUpdate()` because
    * `expandedColumns` is a plain class field (not React state), so React
    * would not otherwise know a re-render is needed.
    */
   toggleExpandedColumns = (): void => {
      this.expandedColumns = !this.expandedColumns;
      this.forceUpdate();
   };

   /**
    * Sets the active sort key and direction, then forces a re-render.
    * Clicking the same column header twice toggles asc ⇔ desc.
    * Sort state lives in class fields (not React state) to avoid
    * unnecessary context propagation on every sort interaction.
    */
   handleSort = (key: SortableKey): void => {
      if (this.sortKey === key) {
         this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
         this.sortKey = key;
         this.sortDir = 'asc';
      }
      this.forceUpdate();
   };

   /**
    * Returns a sorted copy of the row array. Null values always sort to the end
    * regardless of sort direction so they don't cluster at the top when descending.
    */
   getSortedData(rows: ApoHoloResultRow[]): ApoHoloResultRow[] {
      const data = [...rows];
      if (this.sortKey === null) return data;
      const key = this.sortKey;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      return data.sort((a, b) => {
         const av = a[key];
         const bv = b[key];
         if (av === null && bv === null) return 0;
         if (av === null) return 1;
         if (bv === null) return -1;
         return (av - bv) * dir;
      });
   }

   /** Returns the sort indicator character(s) to display next to a column header. */
   sortIndicator(key: SortableKey): string {
      if (this.sortKey !== key) return ' ⇅';
      return this.sortDir === 'asc' ? ' ▲' : ' ▼';
   }

   constructor(props: SitesTableCommonProps, context: React.ContextType<typeof AhojTableContext>) {
      super(props);
      this.context = context;
   }

   /**
    * Subscribes to the representation-change bus and to Molstar structure-click events
    * on mount. The structure-click handler:
    * 1. Scrolls the clicked row into view (150 ms delay to let React render first).
    * 2. Calls `selectUnselectStructureInternal` to sync the table selection state.
    * 3. Skips the event if `suppressNextStructureClick` is set (avoids feedback loops
    *    when the table itself is the source of the click).
    */
   componentDidMount(): void {
      this.context.subscribeRepresentationChange(this.onRepresentationChanged);
      this.context.subscribeStructureClick((rowDto: ApoHoloSiteDto) => {
         if (this.suppressNextStructureClick) {
            this.suppressNextStructureClick = false;
            return;
         }
         if (rowDto) {
            this.props.onSelectStructure();
            this.selectUnselectStructureInternal(rowDto.url, rowDto.id);
            setTimeout(() => {
               const el = document.querySelector<HTMLElement>(`[data-row-id="${rowDto.id}"]`);
               el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }, 150);
         }
      });
   }

   addOrRemoveFromRcsbButtonTitle(): string {
      if (this.context.selectedPdb.some((s) => s.rowId === this.editingRowId)) {
         return this.removeFromRcsbTitle;
      }
      return this.addToRcsbTitle;
   }

   /** Adds or removes the given row from the RCSB sequence viewer's PDB track list. */
   addOrRemoveFromRcsb = (row: ApoHoloResultRow): void => {
      if (this.context.selectedPdb.some((s) => s.rowId === row.row_id)) {
         this.context.setSelectedPdb(this.context.selectedPdb.filter((s) => s.rowId !== row.row_id));
      } else {
         this.context.setSelectedPdb([
            ...this.context.selectedPdb,
            {
               rowId: row.row_id,
               pdbOrUniprotId: row.pdb_id,
               chainId: row.chains?.[0],
            } as SelectStructureDto,
         ]);
      }
   };

   /**
    * Toggles the edit panel for `rowId`. Edit and info panels are mutually exclusive:
    * opening the edit panel closes any open info panel.
    * `e.stopPropagation()` prevents the row's click handler from also firing.
    */
   toggleEditPanel = (rowId: string, e: React.MouseEvent): void => {
      e.stopPropagation();
      const opening = this.context.editingRowId !== rowId;
      this.context.setEditingRowId(opening ? rowId : null);
      if (opening) this.context.setInfoRowId(null);
   };

   /**
    * Toggles the info panel for `rowId`. Edit and info panels are mutually exclusive:
    * opening the info panel closes any open edit panel.
    */
   toggleInfoPanel = (rowId: string, e: React.MouseEvent): void => {
      e.stopPropagation();
      const opening = this.context.infoRowId !== rowId;
      this.context.setInfoRowId(opening ? rowId : null);
      if (opening) this.context.setEditingRowId(null);
   };

   /** Builds a `row_id → url` lookup from all site lists. */
   private getSiteUrlById(): Map<string, string> {
      const { apoHoloSites } = this.context;
      const allSites = [
         ...apoHoloSites.apo_sites,
         ...apoHoloSites.holo_sites,
         ...apoHoloSites.unobserved_sites,
         ...apoHoloSites.query_pocket_sites,
         ...apoHoloSites.alphafold_sites,
      ];
      return new Map(allSites.map((s) => [s.id, s.url]));
   }

   /**
    * Recomputes transparency for all visible rows based on whether any visible row has a
    * color pair active. The row whose state was just updated via setControl (and thus not
    * yet reflected in this.context.controlsMap) is handled via the override parameters.
    *
    * Rules:
    *   - no visible row active → all visible rows opaque (1.0)
    *   - any visible row active → active rows opaque, inactive rows semi-transparent
    *
    * Multiple rows can share the same structure file URL (e.g. different chains of the
    * same PDB entry). Because SET_TRANSPARENCY targets the Molstar structure label
    * (derived from the URL), per-row dispatches for a shared URL would overwrite each
    * other in iteration order — whichever row is processed last wins. To avoid this
    * last-write-wins race, decisions are first collapsed to a per-URL map: if ANY
    * visible row for a given URL is pair-active, that structure stays opaque.
    */
   private recomputeAllTransparencies(overrideRowId: string, overrideActive: boolean): void {
      const siteUrlById = this.getSiteUrlById();

      // Determine whether any visible row is active, considering the pending override.
      // Hidden rows with stale pair colors must not contribute.
      let anyActive = overrideActive;
      if (!anyActive) {
         this.context.controlsMap.forEach((state, rowId) => {
            if (!state.isVisible) return;
            if (rowId === overrideRowId) return; // handled by override
            if (this.isColorPairActive(rowId)) anyActive = true;
         });
      }

      // Collapse per-row pair-active decisions to per-URL using OR logic so that a
      // pair-active row is never overwritten by a non-active row that shares its URL.
      const urlActiveMap = new Map<string, boolean>();
      this.context.controlsMap.forEach((state: ControlState, rowId: string) => {
         if (!state.isVisible) return;
         const url = siteUrlById.get(rowId);
         if (!url) return;
         const isPairActive = rowId === overrideRowId ? overrideActive : this.isColorPairActive(rowId);
         urlActiveMap.set(url, (urlActiveMap.get(url) ?? false) || isPairActive);
      });

      urlActiveMap.forEach((isPairActive, url) => {
         if (!anyActive || isPairActive) {
            this.context.dispatch({
               type: 'SET_TRANSPARENCY',
               payload: { url, alpha: 1.0 } as MolstarTransparencyPayload,
            } as MolstarAction);
         } else {
            this.context.dispatch({
               type: 'SET_TRANSPARENCY',
               payload: { url, alpha: 0.05, componentType: 'pocket' } as MolstarTransparencyPayload,
            } as MolstarAction);
            this.context.dispatch({
               type: 'SET_TRANSPARENCY',
               payload: { url, alpha: 0.2, componentType: 'chain' } as MolstarTransparencyPayload,
            } as MolstarAction);
            this.context.dispatch({
               type: 'SET_TRANSPARENCY',
               payload: { url, alpha: 0.2, componentType: 'ligand' } as MolstarTransparencyPayload,
            } as MolstarAction);
         }
      });
   }

   getGlobalColorPairForStructureType = (structureType: StructureType): ColorPairDto => {
      const group = getStructureTypeGroup(structureType);
      if (group === 'alphafold') {
         return {
            pocketColor: this.context.globalAlpafoldPocketColor,
            restColor: this.context.globalAlpafoldRestColor,
         };
      } else if (group === 'apo') {
         return { pocketColor: this.context.globalApoPocketColor, restColor: this.context.globalApoRestColor };
      } else {
         // holo group: holo, unobserved, query_pocket
         return { pocketColor: this.context.globalHoloPocketColor, restColor: this.context.globalHoloRestColor };
      }
   };

   getGlobalColorPair = (): ColorPairDto => {
      const structureType = this.props.structureType;
      return this.getGlobalColorPairForStructureType(structureType);
   };

   /**
    * Returns `true` when this row's colors differ from its per-group global defaults,
    * meaning a quick-color pair is active for it.
    *
    * Uses the row's own `structureType` (from `controlsMap`) rather than
    * `this.props.structureType` so that `recomputeAllTransparencies`, which iterates
    * over ALL rows regardless of which table it is called from, produces correct
    * results for every row type.
    */
   isColorPairActive = (rowId: string): boolean => {
      const state = this.context.controlsMap.get(rowId);
      if (!state) return false;
      const { pocketColor: globalPocketColor, restColor: globalRestColor } = this.getGlobalColorPairForStructureType(
         state.structureType,
      );
      return state.pocketColor !== globalPocketColor || state.chainColor !== globalRestColor;
   };

   /** Returns `true` if any row in `rows` currently has a custom quick-color pair active. */
   hasAnyColorPairActive = (rows: ApoHoloResultRow[]): boolean => {
      return rows.some((r) => this.isColorPairActive(r.row_id));
   };

   /**
    * Toggles a deterministic quick color-pair for the given row.
    *
    * **ACTIVATE** (row has no active pair):
    * 1. Finds the color-pair index with the lowest current usage count.
    * 2. Applies that pair's pocket and chain colors to the row's state.
    * 3. If the row was hidden, makes it visible first.
    * 4. Calls `recomputeAllTransparencies` so non-paired rows become semi-transparent.
    * 5. Dispatches two `dispatchRepresentationChange` events (pocket + chain) with
    *    `postAlpha: 1.0` so the newly colored row stays fully opaque.
    *
    * **DEACTIVATE** (row already has an active pair):
    * 1. Resets the row's colors to the current global defaults.
    * 2. Determines whether any OTHER visible rows still have an active pair.
    * 3. Uses `postAlpha` to atomically apply the correct transparency right after
    *    `updateRepresentationsTheme` resolves (which would otherwise reset alpha to 1.0).
    * 4. Decrements the color-pair usage count.
    *
    * The `overrideRowId`/`overrideActive` parameters to `recomputeAllTransparencies`
    * are needed because `setControl` is async: the new state may not yet be in
    * `this.context.controlsMap` when `recomputeAllTransparencies` reads it.
    */
   selectNextColorPair = (row: ApoHoloResultRow): void => {
      if (!row.structure_file_url) return;

      const url = `${API_URL}/${row.structure_file_url}`;

      if (this.isColorPairActive(row.row_id)) {
         this.resetToGlobalColors(row, url);
         return;
      }

      this.activateColorPair(row, url);
   };

   private resetToGlobalColors = (row: ApoHoloResultRow, url: string): void => {
      const currentState = this.context.controlsMap.get(row.row_id);
      const newState =
         currentState === undefined ? fromStructureType(this.props.structureType) : fromControlState(currentState);
      this.context.colorPairCounts.set(
         currentState?.quickColorIndex ?? -1,
         Math.max(0, (this.context.colorPairCounts.get(currentState?.quickColorIndex ?? -1) ?? 0) - 1),
      );
      // Reset to the per-group global defaults (not the generic global), so that
      // isColorPairActive correctly sees this row as "inactive" after the reset.
      const globalColors = this.getGlobalColorPairForStructureType(newState.structureType);
      newState.pocketColor = globalColors.pocketColor;
      newState.chainColor = globalColors.restColor;
      newState.quickColorIndex = undefined;
      this.context.setControl(row.row_id, newState);

      // Determine what alpha this row should have after deactivation.
      // If other visible rows still have a pair active, this row becomes transparent.
      // We need to know this BEFORE recomputeAllTransparencies so we can embed the
      // target alpha directly in the representation-change payload and apply it
      // atomically AFTER updateRepresentationsTheme resolves (which resets alpha to 1.0).
      const anyOtherVisibleActive = [...this.context.controlsMap.entries()].some(
         ([id, st]) => id !== row.row_id && st.isVisible && this.isColorPairActive(id),
      );
      const postAlphaPocket = anyOtherVisibleActive ? 0.05 : 1.0;
      const postAlphaRest = anyOtherVisibleActive ? 0.2 : 1.0;

      this.context.dispatchRepresentationChange({
         type: newState.selectedRepresentations.get('pocket') ?? defaultPocketRepresentation,
         color: globalColors.pocketColor,
         rowId: row.row_id,
         allStructuresOfSingleType: false,
         url,
         structureComponentType: 'pocket',
         representationChanged: false,
         colorChanged: true,
         skipControlsSync: true,
         postAlpha: postAlphaPocket,
         structureType: this.props.structureType,
      });
      this.context.dispatchRepresentationChange({
         type: newState.selectedRepresentations.get('chain') ?? defaultRestRepresentation,
         color: globalColors.restColor,
         rowId: row.row_id,
         allStructuresOfSingleType: false,
         url,
         structureComponentType: 'chain',
         representationChanged: false,
         colorChanged: true,
         skipControlsSync: true,
         postAlpha: postAlphaRest,
         structureType: this.props.structureType,
      });

      // Recompute transparency for all OTHER visible rows (the reset row's alpha is
      // handled atomically above via postAlpha, so pass its override as false here).
      this.recomputeAllTransparencies(row.row_id, false);
   };

   private activateColorPair = (row: ApoHoloResultRow, url: string): void => {
      const nextIndex =
         this.context.colorPairCounts.size === 0
            ? 0
            : [...this.context.colorPairCounts.entries()].reduce((minEntry, entry) => {
                 return entry[1] < minEntry[1] ? entry : minEntry;
              })[0];

      const pair = COLOR_PAIRS[nextIndex];

      const currentState = this.context.controlsMap.get(row.row_id);
      const newState =
         currentState === undefined ? fromStructureType(this.props.structureType) : fromControlState(currentState);
      const wasVisible = newState.isVisible;
      newState.pocketColor = pair.pocket;
      newState.chainColor = pair.chain;
      newState.quickColorIndex = nextIndex;

      this.context.colorPairCounts.set(nextIndex, (this.context.colorPairCounts.get(nextIndex) ?? 0) + 1);

      if (!wasVisible) {
         newState.isVisible = true;
         this.context.setControl(row.row_id, newState);
         this.context.dispatch({
            type: 'TOGGLE_VISIBILITY',
            payload: { rowId: row.row_id, url, value: true },
         } as MolstarAction);
         // The new row isn't visible yet in controlsMap, but we pass it as the override
         // so recomputeAllTransparencies knows to treat it as active (anyActive = true).
      } else {
         this.context.setControl(row.row_id, newState);
      }

      // Recompute with this row treated as active regardless of whether setControl
      // has propagated through React yet.
      this.recomputeAllTransparencies(row.row_id, true);

      this.context.dispatchRepresentationChange({
         type: newState.selectedRepresentations.get('pocket') ?? defaultPocketRepresentation,
         color: pair.pocket,
         rowId: row.row_id,
         allStructuresOfSingleType: false,
         url,
         structureComponentType: 'pocket',
         representationChanged: false,
         colorChanged: true,
         skipControlsSync: true,
         postAlpha: 1.0,
         structureType: this.props.structureType,
      });
      this.context.dispatchRepresentationChange({
         type: newState.selectedRepresentations.get('chain') ?? defaultRestRepresentation,
         color: pair.chain,
         rowId: row.row_id,
         allStructuresOfSingleType: false,
         url,
         structureComponentType: 'chain',
         representationChanged: false,
         colorChanged: true,
         skipControlsSync: true,
         postAlpha: 1.0,
         structureType: this.props.structureType,
      });
   };

   /**
    * Resets all rows to their default global colors and clears all quick-color pair
    * assignments. Dispatches per-group pocket and chain color updates, then one global
    * ligand update with `resetTransparency: true` so Molstar re-applies alpha 1.0 to
    * every visible structure after that color update.
    */
   resetAllColors = (e: React.MouseEvent): void => {
      e.stopPropagation();

      this.context.colorPairCounts.forEach((_, index) => {
         this.context.colorPairCounts.set(index, 0);
      });

      // Reset every row to its own per-group global color.
      this.context.controlsMap.forEach((state: ControlState, rowId: string) => {
         const newState = fromControlState(state);
         const globalColors = this.getGlobalColorPairForStructureType(newState.structureType);
         newState.pocketColor = globalColors.pocketColor;
         newState.chainColor = globalColors.restColor;
         newState.ligandColor = getDefaultLigandColor();
         newState.quickColorIndex = undefined;
         this.context.setControl(rowId, newState);
      });

      // Dispatch per-group visual updates so Molstar resets all three type groups.
      // skipControlsSync: true because controlsMap was already updated above.
      const groups = ['apo', 'holo', 'alphafold'] as const;
      for (const group of groups) {
         const groupColors = this.getGlobalColorPairForStructureType(group);
         this.context.dispatchRepresentationChange({
            type: 'cartoon',
            color: groupColors.pocketColor,
            allStructuresOfSingleType: true,
            url: '',
            structureComponentType: 'pocket',
            representationChanged: false,
            colorChanged: true,
            skipControlsSync: true,
            structureType: group,
         } as MolstarRepresentationChangePayload);
         this.context.dispatchRepresentationChange({
            type: 'cartoon',
            color: groupColors.restColor,
            allStructuresOfSingleType: true,
            url: '',
            structureComponentType: 'chain',
            representationChanged: false,
            colorChanged: true,
            skipControlsSync: true,
            structureType: group,
         } as MolstarRepresentationChangePayload);
      }

      // Reset ligand color and transparency for all structures in one global event.
      this.context.dispatchRepresentationChange({
         type: defaultLigandRepresentation,
         color: getDefaultLigandColor(),
         allStructuresOfSingleType: false,
         allStructures: true,
         url: '',
         structureComponentType: 'ligand',
         representationChanged: false,
         colorChanged: true,
         skipControlsSync: true,
         resetTransparency: true,
      } as MolstarRepresentationChangePayload);
   };

   /**
    * Dispatches a per-row color change for the given component type.
    * Also resets transparency to 1.0 before the color change so the structure
    * is not left in a faded state from a previous color-pair de-emphasis.
    */
   changeColor = (row: ApoHoloResultRow, color: string, structureComponentType?: StructureComponentType): void => {
      if (row.structure_file_url === undefined || row.structure_file_url === null) return;

      const currentState = this.context.controlsMap.get(row.row_id);
      const newState =
         currentState === undefined ? fromStructureType(this.props.structureType) : fromControlState(currentState);
      newState.pocketColor =
         structureComponentType === 'pocket' || structureComponentType === undefined ? color : newState.pocketColor;
      newState.chainColor =
         structureComponentType === 'chain' || structureComponentType === undefined ? color : newState.chainColor;
      newState.ligandColor =
         structureComponentType === 'ligand' || structureComponentType === undefined ? color : newState.ligandColor;

      this.context.dispatch({
         type: 'SET_TRANSPARENCY',
         payload: { url: `${API_URL}/${row.structure_file_url}`, alpha: 1.0 } as MolstarTransparencyPayload,
      } as MolstarAction);

      this.context.dispatchRepresentationChange({
         type:
            newState.selectedRepresentations.get(structureComponentType ?? 'chain') ??
            (structureComponentType === 'pocket'
               ? defaultPocketRepresentation
               : structureComponentType === 'ligand'
                 ? defaultLigandRepresentation
                 : defaultRestRepresentation),
         color,
         rowId: row.row_id,
         allStructuresOfSingleType: false,
         url: `${API_URL}/${row.structure_file_url}`,
         structureComponentType: structureComponentType ?? 'chain',
         representationChanged: false,
         colorChanged: true,
      } as MolstarRepresentationChangePayload);
   };

   /**
    * Syncs `controlsMap` after a representation/color change is received from the event
    * bus. Skipped when `payload.skipControlsSync` is `true` (e.g. when the table itself
    * was the origin of the change and has already updated its own state).
    *
    * Handles both global (`all: true`) and per-row changes.
    */
   onRepresentationChanged = (payload: MolstarRepresentationChangePayload): void => {
      if (payload.skipControlsSync) return;
      if (payload.allStructures || payload.allStructuresOfSingleType) {
         this.context.controlsMap.forEach((state: ControlState, rowId: string) => {
            // For per-group events, only update rows whose type belongs to the target group.
            if (payload.allStructuresOfSingleType && payload.structureType) {
               const group = getStructureTypeGroup(state.structureType);
               if (group !== payload.structureType) return;
            }
            const newState = fromControlState(state);
            if (payload.representationChanged) {
               newState.selectedRepresentations.set(payload.structureComponentType ?? 'chain', payload.type);
            }
            if (payload.colorChanged) {
               if (payload.structureComponentType === 'pocket') {
                  newState.pocketColor = payload.color;
               } else if (payload.structureComponentType === 'ligand') {
                  newState.ligandColor = payload.color;
               } else {
                  newState.chainColor = payload.color;
               }
            }
            this.context.setControl(rowId, newState);
         });
         return;
      }

      if (payload.rowId === undefined) return;

      const state = this.context.controlsMap.get(payload.rowId);

      if (state === undefined) return;

      const newState = fromControlState(state);
      if (payload.representationChanged) {
         newState.selectedRepresentations.set(payload.structureComponentType ?? 'chain', payload.type);
      }
      if (payload.colorChanged) {
         if (payload.structureComponentType === 'pocket') {
            newState.pocketColor = payload.color;
         } else if (payload.structureComponentType === 'ligand') {
            newState.ligandColor = payload.color;
         } else {
            newState.chainColor = payload.color;
         }
      }

      this.context.setControl(payload.rowId, newState);
   };

   /**
    * Dispatches a per-row representation change. Also includes `colorChanged: true`
    * so the current row color is re-applied alongside the new representation type,
    * keeping the Molstar state consistent (changing representation rebuilds the
    * representation params object and would otherwise reset the color to default).
    */
   changeRepresentation = (
      type: Representation,
      row: ApoHoloSiteRow,
      structureComponentType: StructureComponentType,
   ): void => {
      if (row.structure_file_url === undefined || row.structure_file_url === null) return;

      const currentState = this.context.controlsMap.get(row.row_id);
      const newState =
         currentState === undefined ? fromStructureType(this.props.structureType) : fromControlState(currentState);
      newState.selectedRepresentations.set(structureComponentType ?? 'chain', type);

      this.context.dispatchRepresentationChange({
         type,
         color:
            structureComponentType === 'pocket'
               ? newState.pocketColor
               : structureComponentType === 'ligand'
                 ? newState.ligandColor
                 : newState.chainColor,
         rowId: row.row_id,
         allStructuresOfSingleType: false,
         url: `${API_URL}/${row.structure_file_url}`,
         structureComponentType,
         representationChanged: true,
         colorChanged: true,
      } as MolstarRepresentationChangePayload);
   };

   /**
    * Core visibility / focus toggle. Called both by row-click (`selectUnselectStructure`)
    * and by the Molstar structure-click handler (`componentDidMount` subscription).
    *
    * Tricky: if the structure is not yet visible, the action sets it visible AND sets
    * `focusAfterLoad: true` so `MolstarComponent` will focus it once loading completes.
    * If it is already visible, only the selection (camera focus) is toggled.
    */
   selectUnselectStructureInternal = (url: string, rowId: string): void => {
      if (url === undefined || url === null) return;

      const currentState = this.context.controlsMap.get(rowId);
      const newState =
         currentState === undefined ? fromStructureType(this.props.structureType) : fromControlState(currentState);

      // If the structure is hidden, make it visible first and focus after loading
      if (!newState.isVisible) {
         newState.isVisible = true;
         newState.isSelected = true;
         this.context.setControl(rowId, newState);
         this.context.dispatch({
            type: 'TOGGLE_VISIBILITY',
            payload: {
               rowId,
               url: `${API_URL}/${url}`,
               value: true,
               focusAfterLoad: true,
            },
         } as MolstarAction);
         return;
      }

      newState.isSelected = !newState.isSelected;
      this.context.setControl(rowId, newState);

      this.context.dispatch({
         type: 'FOCUS',
         payload: {
            rowId,
            url: `${API_URL}/${url}`,
            value: newState.isSelected,
         },
      } as MolstarAction);
   };

   /**
    * Public row-click entrypoint. Delegates to `selectUnselectStructureInternal` and
    * then dispatches a `structureClick` event so other tables and `SequenceViewerComponent`
    * can sync their selection state. Sets `suppressNextStructureClick` to prevent the
    * event from feeding back into this table's own structure-click subscriber.
    */
   selectUnselectStructure = (row: ApoHoloResultRow): void => {
      this.selectUnselectStructureInternal(row.structure_file_url, row.row_id);
      const allSites = this.context.apoHoloSites;
      const site = [
         ...allSites.apo_sites,
         ...allSites.holo_sites,
         ...allSites.unobserved_sites,
         ...allSites.query_pocket_sites,
         ...allSites.alphafold_sites,
      ].find((s) => s.id === row.row_id);
      if (site) {
         this.suppressNextStructureClick = true;
         this.context.dispatchStructureClick(site);
      }
   };

   /**
    * Batch show/hide toggle for a list of rows.
    * - If any row is currently visible: hides all of them.
    * - If none are visible: shows the first 20 rows (plus any that already have
    *   a `controlsMap` entry, meaning they were previously loaded).
    */
   showHideAll = (rows: ApoHoloResultRow[]): void => {
      const anyVisible = rows.some((row) => this.context.controlsMap.get(row.row_id)?.isVisible);

      if (anyVisible) {
         const rowsToHide = rows.filter((row) => row.structure_file_url && this.context.controlsMap.has(row.row_id));
         rowsToHide.forEach((row) => {
            const currentState = this.context.controlsMap.get(row.row_id);
            const newState =
               currentState === undefined
                  ? fromStructureType(this.props.structureType)
                  : fromControlState(currentState);
            newState.isVisible = false;
            newState.isSelected = false;
            this.context.setControl(row.row_id, newState);
            this.context.dispatch({
               type: 'TOGGLE_VISIBILITY',
               payload: { rowId: row.row_id, url: `${API_URL}/${row.structure_file_url}`, value: false },
            } as MolstarAction);
            this.context.dispatch({
               type: 'FOCUS',
               payload: { rowId: row.row_id, url: `${API_URL}/${row.structure_file_url}`, value: false },
            } as MolstarAction);
         });
      } else {
         const first20Ids = new Set(rows.slice(0, 20).map((r) => r.row_id));
         const toShow = rows.filter((row) => first20Ids.has(row.row_id) || this.context.controlsMap.has(row.row_id));
         toShow.forEach((row) => {
            const currentState = this.context.controlsMap.get(row.row_id);
            const newState =
               currentState === undefined
                  ? fromStructureType(this.props.structureType)
                  : fromControlState(currentState);
            newState.isVisible = true;
            newState.isSelected = false;
            this.context.setControl(row.row_id, newState);
            this.context.dispatch({
               type: 'TOGGLE_VISIBILITY',
               payload: { rowId: row.row_id, url: `${API_URL}/${row.structure_file_url}`, value: true },
            } as MolstarAction);
         });
      }
   };

   /**
    * Per-row eye-icon toggle. Hiding a row also clears its selection so the camera
    * does not try to focus a hidden structure.
    */
   showHideStructure = (row: ApoHoloResultRow): void => {
      if (row.structure_file_url === undefined || row.structure_file_url === null) return;

      const currentState = this.context.controlsMap.get(row.row_id);
      const newState =
         currentState === undefined ? fromStructureType(this.props.structureType) : fromControlState(currentState);
      newState.isVisible = !newState.isVisible;

      if (!newState.isVisible) {
         newState.isSelected = false;
         this.context.setControl(row.row_id, newState);
         this.context.dispatch({
            type: 'TOGGLE_VISIBILITY',
            payload: { rowId: row.row_id, url: `${API_URL}/${row.structure_file_url}`, value: false },
         } as MolstarAction);
         this.context.dispatch({
            type: 'FOCUS',
            payload: { rowId: row.row_id, url: `${API_URL}/${row.structure_file_url}`, value: false },
         } as MolstarAction);
      } else {
         this.context.setControl(row.row_id, newState);
         this.context.dispatch({
            type: 'TOGGLE_VISIBILITY',
            payload: { rowId: row.row_id, url: `${API_URL}/${row.structure_file_url}`, value: true },
         } as MolstarAction);
      }
   };
}
